import { SlideSection } from "@NextSkyTheme/slide";
import { LazyLoader } from "@NextSkyTheme/lazy-load";
import * as NextSkyTheme from "@NextSkyTheme/global";
import { eventModal } from "@NextSkyTheme/modal";

/**
 * Apparel (and swag) keyword detection for predictive search CTA
 */
const APPAREL_KEYWORDS = [
  "shirt",
  "shirts",
  "hoodie",
  "hoodies",
  "tee",
  "tees",
  "t-shirt",
  "t shirt",
  "crewneck",
  "sweatshirt",
  "apparel",
  "clothing",
  "merch",
  "swag",
  "long sleeve",
  "long sleeve tee",
  "mug",
  "travel",
  "travel mug",
  "phone",
  "phone case",
  "drawstring",
  "tote",
  "coaster",
  "socks",
  "hat",
  "beanie",
  "tumbler",
  "kids",
];

class ActionSearch extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.input = this.querySelector('input[type="search"]');
    this.form =
      this.querySelector("form#search_mini_form") ||
      this.querySelector("form.search") ||
      this.querySelector("form");

    this.templateRecommendedDefault = this.querySelector(
      ".template-recommended-default"
    );
    this.templateRecommendedPopup = this.querySelector(
      ".template-recommended-popup"
    );

    this.predictiveSearchResults = this.querySelector(".predictive-search");
    this.searchKeyword = this.querySelector(".search-keyword");
    this.recommendSearch = this.querySelector(".recommend-search");

    // Input listeners (guarded)
    this.input?.addEventListener(
      "input",
      NextSkyTheme.debounce((event) => {
        this.onInput(event);
      }, 300).bind(this)
    );

    this.input?.addEventListener("click", this.hideAnimationText.bind(this));

    // Theme editor support
    if (window.Shopify?.designMode) {
      document.addEventListener("shopify:section:select", (event) => {
        const currentTarget = event.target;
        if (!currentTarget?.dataset?.shopifyEditorSection) return;

        const sectionData = JSON.parse(currentTarget.dataset.shopifyEditorSection);
        if (sectionData?.id === this.sectionId) {
          eventModal(this, "open", false);

          const anim = this.querySelector(".search-animation");
          if (anim && anim.classList.contains("hidden-important")) {
            setTimeout(() => this.input?.focus(), 10);
          }
        }
      });

      document.addEventListener("shopify:section:deselect", () => {
        const searchSection = document.querySelector("action-search");
        if (searchSection) eventModal(searchSection, "close", false);
      });
    }
  }

  get actionSearch() {
    return document.querySelector("#search-icon-bubble") || null;
  }

  get type() {
    return this.getAttribute("data-type");
  }

  connectedCallback() {
    if (this.actionSearch) {
      this.actionSearch.addEventListener("click", (event) => {
        event.preventDefault();

        if (
          this.recommendSearch &&
          this.recommendSearch.querySelector("template") &&
          this.templateRecommendedDefault
        ) {
          this.updateProductRecommended();
        }

        eventModal(this, "open", false);
        setTimeout(() => this.input?.focus(), 10);
        NextSkyTheme.global.rootToFocus = this.actionSearch;
      });
    }

    if (this.type === "popup") {
      this.innerWidth();
    }
  }

  innerWidth() {
    let width = window.innerWidth;

    window.addEventListener("resize", () => {
      const newWidth = window.innerWidth;

      if (newWidth <= 1024 && width > 1024) this.actionSearchMobile();
      if (newWidth > 1024 && width <= 1024) this.actionSearchDesktop();

      width = newWidth;
    });

    if (width <= 1024) this.actionSearchMobile();
    else this.actionSearchDesktop();
  }

  updateProductRecommended() {
    if (!this.recommendSearch) return;

    if (this.classList.contains("search-type-popup") && this.templateRecommendedPopup) {
      const content = document.createElement("div");
      content.appendChild(
        this.templateRecommendedPopup.content.firstElementChild.cloneNode(true)
      );
      this.recommendSearch.innerHTML = content.innerHTML;
    } else if (this.templateRecommendedDefault) {
      const content = document.createElement("div");
      content.appendChild(
        this.templateRecommendedDefault.content.firstElementChild.cloneNode(true)
      );
      this.recommendSearch.innerHTML = content.innerHTML;
    }

    this.recommendSearch.classList.add("update-recommended");
    new LazyLoader(".image-lazy-load");
  }

  actionSearchMobile() {
    this.classList.remove("search-type-popup");
    this.classList.add("search-type-drawer");
    this.onInput();

    if (
      this.templateRecommendedDefault &&
      this.recommendSearch?.classList.contains("update-recommended")
    ) {
      this.updateProductRecommended();
    }
  }

  actionSearchDesktop() {
    this.classList.remove("search-type-drawer");
    this.classList.add("search-type-popup");
    this.onInput();

    if (
      this.templateRecommendedDefault &&
      this.recommendSearch?.classList.contains("update-recommended")
    ) {
      this.updateProductRecommended();
    }
  }

  onInput() {
    this.hideAnimationText();

    const searchTerm = (this.input?.value || "").trim();

    if (!searchTerm.length) {
      // Important: remove CTA when input is cleared
      this.removeApparelCta();

      this.form?.classList.remove("results", "on-input", "loading");
      NextSkyTheme.trapFocus(this);
      return;
    }

    let predictiveSearch = "predictive-search-drawer";
    if (this.classList.contains("search-type-popup")) {
      predictiveSearch = "predictive-search-popup";
    }

    this.form?.classList.add("loading", "on-input");

    if (this.predictiveSearchResults) {
      this.getSearchResults(searchTerm, predictiveSearch);
      return;
    }

    NextSkyTheme.trapFocus(this);
  }

  getSearchResults(searchTerm, predictiveSearch) {
    const query = `${routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&section_id=${predictiveSearch}&resources[limit]=10&resources[limit_scope]=each`;

    fetch(query)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(String(response.status));
          this.form?.classList.remove("loading", "results");
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const resultsMarkup =
          doc.querySelector(`#shopify-section-${predictiveSearch}`)?.innerHTML;

        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.innerHTML = resultsMarkup || "";

          // Insert apparel CTA after results render
          this.insertApparelCta(searchTerm);
        }

        this.form?.classList.remove("loading");
        this.form?.classList.add("results");

        // Fix "more results" links
        const moreLinks = this.querySelectorAll(".more-result-link");
        if (moreLinks?.length) {
          moreLinks.forEach((e) => {
            e.href = `${routes.search_url}?q=${encodeURIComponent(
              searchTerm
            )}&options%5Bprefix%5D=last`;
          });
        }

        NextSkyTheme.trapFocus(this);
      })
      .catch((error) => {
        this.form?.classList.remove("loading", "results");
        console.error("Predictive search error:", error);
        NextSkyTheme.trapFocus(this);
      });
  }

  isApparelQuery(q) {
    const query = (q || "").toLowerCase().trim();
    if (query.length < 2) return false;
    return APPAREL_KEYWORDS.some((k) => query.includes(k));
  }

  removeApparelCta() {
    if (!this.predictiveSearchResults) return;
    const existing = this.predictiveSearchResults.querySelector(
      '[data-apparel-cta="true"]'
    );
    if (existing) existing.remove();
  }

  insertApparelCta(searchTerm) {
    if (!this.predictiveSearchResults) return;

    this.removeApparelCta();
    if (!this.isApparelQuery(searchTerm)) return;

    const link = `https://www.chubblegumswag.com/search?q=${encodeURIComponent(
      searchTerm || ""
    )}`;

    const el = document.createElement("div");
    el.setAttribute("data-apparel-cta", "true");
    el.className = "apparel-cta";
    el.innerHTML = `
      <div class="apparel-cta__inner">
        <div class="apparel-cta__text">
          <div class="apparel-cta__title">LOOKING FOR APPAREL?</div>
          <div class="apparel-cta__sub">Shop it on chubblegumswag.com</div>
        </div>
        <a class="apparel-cta__link" href="${link}" target="_blank" rel="noopener">
          Shop Apparel
        </a>
      </div>
    `;

    this.predictiveSearchResults.prepend(el);
  }

  hideAnimationText() {
    const anim = this.querySelector(".search-animation");
    if (anim) anim.classList.add("hidden-important");
  }
}

customElements.define("action-search", ActionSearch);

class SearchText extends HTMLElement {
  constructor() {
    super();
    Motion.inView(this, this.init.bind(this));
  }

  async init() {
    await this.animation(this.getAttribute("data-text"));
  }

  async animation(text) {
    this.innerHTML = text || "";

    await Motion.animate(
      this,
      { width: [0, `${this.scrollWidth + 1}px`] },
      { duration: 0.7, delay: 0.5 }
    ).finished;

    const cursor = this.closest(".search-animation")?.querySelector(".text-cursor");
    cursor?.classList.remove("hidden-important");
  }
}

customElements.define("search-text", SearchText);
